import registerJQueryPlugin from '../../lib/register-jquery-plugin';
import Symbol from 'es6-symbol';
/**
 * Defines default options for a floating LABEL
 * @type {Object}
 */
const FLOATING_LABEL_OPTIONS = {
  block: 'floating-label',
  fieldElements: ['input', 'select', 'textarea'],
  labelElement: 'label',
  callers: [],
};

/**
 * Defines the different types of supported events.
 * @type {Object}
 */
export const EVENTS = {
  FOCUS: 'focus',
  BLUR: 'blur',
  INVALID: 'invalid',
  INPUT: 'input',
  LOAD: 'load',
};

/**
 * Defines the types associated to the elements within a floating label. It
 * uses Symbol as the value so that unique values can be used without caring
 * about conflicts values. Think of this similar to an Enum or an IOTA.
 * @enum
 * @type {Object}
 */
export const TYPES = {
  CONTAINER: Symbol(),
  LABEL: Symbol(),
  FIELD: Symbol(),
};

registerJQueryPlugin('floatingLabel', floatingLabel);

/**
 * Responsible for setting up any caller that needs to hook into the floating
 * LABEL. The goal of this work is to:
 *
 * - Offload responsibility of the interaction so any floating LABEL
 *   functionality can scale horizontally.
 * - Decrease the amount of event bound to the DOM by calling on the listener
 *   provided by the callers.
 * - Consolidate all events for ease of clean up.
 *
 * As part of the setup, a number of requirements are needed for a floating
 * LABEL include:
 *
 * - A CONTAINER element (the one passed in)
 * - A LABEL element
 * - Some kinda of form FIELD element
 *
 * Once all the requirements have been met, floating label will interact with
 * "callers", which serve the following purposes:
 *
 * - Accept the output of elements
 * - Respond to any events dictated by floating label by forwarding along the
 *   event name followed by the event on the form field
 *
 * To do so, a caller is a function where the returned function acts as the
 * responder. In it's simplest form, a caller is a function that returns a
 * function, where the first function passes along all the "nodes" or elements
 * defined in TYPES and the second function (or inner function) it used for
 * listening to/responding to events.
 *
 * @example
 * const myCaller = (blockElement, labelElement, fieldFormElement) => (type, evt) => {
 *   switch(type) {
 *     case 'FOCUS':
 *       blockElement.classList.add('i-do-focus-things');
 *     case 'BLUR':
 *       blockElement.classList.remove('i-do-focus-things');
 *       blockElement.classList.add('i-do-blur-things');
 *   };
 * };
 *
 * floatingLabel($el, {
 *   callers: [myCaller]
 * });
 *
 * @see TYPES
 * @see FLOATING_LABEL_OPTIONS
 * @param {HTMLElement} container - The element containing the LABEL and FIELD
 * @param {Object} options - Options used for overriding
 * @returns {Object} Properties used for interacting after the initial call
 */
export default function floatingLabel(container, options = {}) {
  const {
    block,
    fieldElements,
    labelElement,
    callers,
  } = $.extend({}, FLOATING_LABEL_OPTIONS, options);
  const formField = createFormField(container, block, fieldElements);
  const label = createField(TYPES.LABEL, container, block, labelElement);

  if (!label.node) {
    throw new Error(`No field found in a floating label for ${label.name}`);
  }

  const elements = [
    createField(TYPES.CONTAINER, container, block),
    label,
    formField,
  ];

  const listeners = callers.map(caller => caller(...elements));
  const { node: fieldNode } = formField;
  const focusListener = addListener('focus', fieldNode, listeners, ({ target }) => target.setCustomValidity(''));
  const blurListener = addListener('blur', fieldNode, listeners);
  const invalidListener = addListener('invalid', fieldNode, listeners);
  const inputListener = addListener('input', fieldNode, listeners);
  const loadListener = addListener('load', fieldNode, listeners);
  const eventListeners = [focusListener, blurListener, invalidListener, inputListener, loadListener];

  return {
    removeListeners: () => removeListeners(eventListeners),
  };
}

/**
 * Negotiates and resolves the form FIELD within the CONTAINER. There are a
 * number of possible element names that can be used, so this will ensure that
 * the supported element names correlate to what is in the DOM.
 * @param {HTMLElement} container - Contains the form FIELD element
 * @param {string} block - The block name
 * @param {Array} fieldElements = Possible elements names
 * @returns {Object} The name and node representing the form FIELD.
 */
const createFormField = (container, block, fieldElements) => {
  const [field, ...otherFields] = fieldElements
    .map(element => createField(TYPES.FIELD, container, block, element))
    .filter(({ node }) => node);

  if (otherFields.length) {
    throw new Error('Floating label only supports one field in a container');
  }

  if (!field) {
    throw new Error(`No form field node found in a floating label`);
  }

  return field;
};

/**
 * Creates a FIELD based on the block and optionally supplied element.
 * @param {Symbol} type - Uniquely identify a type of FIELD
 * @param {HTMLElement} container - Contains the child elements
 * @param {string} block - The block name
 * @param {string|null} [element=null] - The element name
 * @returns {Object} The name and node representing the FIELD.
 */
const createField = (type, container, block, element = null) => {
  if (element) {
    const name = `${block}__${element}`;
    return { type, name, node: container.querySelector(`.${name}`) };
  } else {
    return { type, name: block, node: container };
  }
};

/**
 * Creates an event listener and passes back all the necessary properties
 * needed for clean up.
 * @param {string} eventName - The name of the event
 * @param {HTMLElement} node - Element receiving the event
 * @param {Array} listeners - The listeners or callers provided from the outside
 * @param {Function} [fn=e => e] - Allow for internal hooking in of the callback
 * @returns {Object} Properties containing event information
 */
const addListener = (eventName, node, listeners, fn = e => e) => {
  let handler;
  node.addEventListener(eventName, handler = e => {
    fn(e);
    listeners.forEach(listener => listener && listener(eventName, e))
  });

  return { node, eventName, handler };
};

/**
 * Removes all events applied to a single floating LABEL.
 * @param {Object} listeners - Info about each element
 */
const removeListeners = (listeners) => {
  listeners.forEach(({ node, eventName, handler }) =>
    node.removeEventListener(eventName, handler)
  );
};



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/floating-label.js